import { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import {
    Modal, ModalDialog, ModalClose, Typography, Box, Button,
    Input, Select, Option, IconButton, Stack, CircularProgress,
} from '@mui/joy';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import { AppContext } from '../context';
import { APIGetProducts, APIReplenishStock, parseApiError } from '../api';

export default function ReplenishStockModal({ open, onClose, onDone }) {
    const { token, showToast } = useContext(AppContext);
    const [products, setProducts] = useState([]);
    const [loading,  setLoading]  = useState(false);
    const [rows,     setRows]     = useState([{ product_id: '', quantity: '' }]);
    const [comment,  setComment]  = useState('');
    const [saving,   setSaving]   = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const data = await APIGetProducts(token, { is_active: true });
        setProducts(Array.isArray(data) ? data : []);
        setLoading(false);
    }, [token]);

    useEffect(() => {
        if (open) { load(); setRows([{ product_id: '', quantity: '' }]); setComment(''); }
    }, [open, load]);

    const usedIds = useMemo(() => new Set(rows.map(r => r.product_id).filter(Boolean)), [rows]);

    const setRow = (i, field, val) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
    const addRow = () => setRows(prev => [...prev, { product_id: '', quantity: '' }]);
    const delRow = (i) => setRows(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

    const handleSubmit = async () => {
        const items = rows
            .filter(r => r.product_id && parseInt(r.quantity) > 0)
            .map(r => ({ product_id: Number(r.product_id), quantity: parseInt(r.quantity) }));
        if (items.length === 0) { showToast('Заполните хотя бы одну позицию', 'warning'); return; }

        setSaving(true);
        const res = await APIReplenishStock(token, { comment: comment.trim(), items });
        setSaving(false);
        if (res?.error) { showToast(parseApiError(res.error, 'Не удалось оформить привоз'), 'danger'); return; }
        showToast('Привоз оформлен ✓', 'success');
        onDone?.(res);
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 460, width: '100%', p: { xs: 2, sm: 3 },
                maxHeight: '95vh', overflowY: 'auto',
            }}>
                <ModalClose />
                <Typography level="h4" sx={{ fontWeight: 800, mb: 0.3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShippingRoundedIcon /> Оформить привоз материалов
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 1.5 }}>
                    Укажите, сколько чего привезли — остаток на складе увеличится.
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
                ) : (
                    <Stack spacing={1} sx={{ mb: 1.5 }}>
                        {rows.map((r, i) => (
                            <Stack key={i} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Select
                                    size="sm" value={r.product_id} placeholder="Материал"
                                    onChange={(_, v) => setRow(i, 'product_id', v || '')}
                                    sx={{ flex: 1 }}
                                >
                                    {products
                                        .filter(p => !usedIds.has(String(p.id)) || String(p.id) === r.product_id)
                                        .map(p => (
                                            <Option key={p.id} value={String(p.id)}>
                                                {p.name} <span style={{ opacity: 0.6 }}>· ост. {p.stock}</span>
                                            </Option>
                                        ))}
                                </Select>
                                <Input
                                    size="sm" type="number" placeholder="+шт"
                                    value={r.quantity}
                                    onChange={(e) => setRow(i, 'quantity', e.target.value)}
                                    sx={{ width: 90 }}
                                />
                                {rows.length > 1 && (
                                    <IconButton size="sm" variant="plain" color="danger" onClick={() => delRow(i)}>
                                        <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                )}
                            </Stack>
                        ))}
                        <Button size="sm" variant="plain" startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={addRow} sx={{ alignSelf: 'flex-start', fontWeight: 700 }}>
                            Добавить позицию
                        </Button>
                    </Stack>
                )}

                <Input size="sm" placeholder="Комментарий (необязательно)"
                    value={comment} onChange={(e) => setComment(e.target.value)} sx={{ mb: 1.5 }} />

                <Button onClick={handleSubmit} loading={saving} sx={{ fontWeight: 800 }}>
                    Оформить привоз
                </Button>
            </ModalDialog>
        </Modal>
    );
}